import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { accountsApi } from '../utils/accountsApi';
import ModernAccountTreeView from '../components/ModernAccountTreeView';

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.header`
  margin-bottom: 2rem;
  h1 { color: #2d3748; font-size: 2rem; margin-bottom: 0.5rem; }
  p  { color: #718096; font-size: 1.1rem; }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`;

const TreeViewContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
`;

const AccountDetails = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  position: sticky;
  top: 2rem;
  h2 { color: #2d3748; margin-top: 0; padding-bottom: 1rem; border-bottom: 1px solid #edf2f7; }
  .detail-row {
    display: flex;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #f7fafc;
    &:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
    .label { font-weight: 600; color: #4a5568; width: 130px; flex-shrink: 0; }
    .value { color: #2d3748; flex: 1; word-break: break-word;
      &.code { font-family: 'Fira Code', monospace; background: #f7fafc; padding: 0.25rem 0.5rem; border-radius: 4px; display: inline-block; }
    }
  }
`;

const ModernChartOfAccounts = () => {
    const [accounts, setAccounts] = useState(null);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // ModernAccountTreeView expects a flat array and builds its own tree
        accountsApi.list({ limit: 1000, isActive: 'true' })
            .then(res => {
                const items = res.items ?? res;
                setAccounts(items);
                if (items.length > 0) setSelected(items[0]);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <PageContainer>
                <Header><h1>Modern Chart of Accounts</h1><p>Loading accounts…</p></Header>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer>
                <Header><h1>Error</h1><p>Failed to load chart of accounts: {error}</p></Header>
            </PageContainer>
        );
    }

    // Adapt new account shape → shape ModernAccountTreeView expects
    // It uses: Code, AName, Name, FatherCode, Level, BopDbt, BopCrdt
    const adapted = (accounts ?? []).map(a => ({
        ...a,
        Code: a.code,
        AName: a.arabicName || a.name,
        Name: a.name,
        FatherCode: a.parentId ?? null,
        Level: a.level,
        BopDbt: 0,
        BopCrdt: 0,
    }));

    return (
        <PageContainer>
            <Header>
                <h1>Modern Chart of Accounts</h1>
                <p>Interactive and animated account hierarchy</p>
            </Header>

            <ContentWrapper>
                <TreeViewContainer>
                    <ModernAccountTreeView
                        data={adapted}
                        onSelectAccount={setSelected}
                        initialExpandedLevels={2}
                    />
                </TreeViewContainer>

                <AccountDetails>
                    <h2>Account Details</h2>
                    {selected ? (
                        <>
                            <div className="detail-row">
                                <div className="label">Code</div>
                                <div className="value code">{selected.code ?? selected.Code ?? 'N/A'}</div>
                            </div>
                            <div className="detail-row">
                                <div className="label">Arabic Name</div>
                                <div className="value" dir="rtl">{selected.arabicName ?? selected.AName ?? '–'}</div>
                            </div>
                            <div className="detail-row">
                                <div className="label">English Name</div>
                                <div className="value">{selected.name ?? selected.Name ?? '–'}</div>
                            </div>
                            <div className="detail-row">
                                <div className="label">Type</div>
                                <div className="value">{selected.type ?? '–'}</div>
                            </div>
                            <div className="detail-row">
                                <div className="label">Currency</div>
                                <div className="value">{selected.currency?.code ?? '–'}</div>
                            </div>
                            <div className="detail-row">
                                <div className="label">Level</div>
                                <div className="value">{selected.level ?? selected.Level ?? '–'}</div>
                            </div>
                            {selected.entity && (
                                <div className="detail-row">
                                    <div className="label">Entity</div>
                                    <div className="value">{selected.entity.code} – {selected.entity.name}</div>
                                </div>
                            )}
                            {selected.notes && (
                                <div className="detail-row">
                                    <div className="label">Notes</div>
                                    <div className="value">{selected.notes}</div>
                                </div>
                            )}
                        </>
                    ) : (
                        <p>Select an account to view details</p>
                    )}
                </AccountDetails>
            </ContentWrapper>
        </PageContainer>
    );
};

export default ModernChartOfAccounts;
